/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect, useRef } from "react";

import { ModalController, IScreenController } from "../../model/navigation";
import { useUnitySelector } from "../../viewModel";
import { selectActiveScreen } from "../../viewModel/screens";
import { useNavigationEngine } from "./useNavigationEngine";

/**
 * Auto-pushes a ModalController onto the active ScreenController's modal stack
 * when `isOpen` becomes true, and removes it when `isOpen` becomes false or the
 * component unmounts.
 *
 * Modal controllers never call `popModal` directly. They dispatch a Redux action
 * to set `isOpen = false`, and this hook handles the stack cleanup.
 *
 * ## FR-02 fix — push and pop always target the same screen
 *
 * The screen reference is captured **once** at push time into `ownerScreenRef`.
 * All subsequent removes (on close or unmount) read from that captured ref
 * instead of resolving the active screen again.  This guarantees correctness
 * even when a screen transition fires while the modal is open — resolving again
 * would find the NEW screen, but the modal lives on the ORIGINAL screen's
 * stack.
 *
 * `removeModal(name)` is used rather than `popModal(name)` so that a stacked
 * modal sitting above this one never blocks removal.
 *
 * ## Re-targeting when the active screen changes
 *
 * A modal that is open across a screen transition — the global alert overlay
 * and UnityDialog live at the app root and outlive any single screen — must
 * follow the active screen, because input is routed
 * `activeScreen → ScreenController → modal stack`. The effect therefore also
 * depends on `activeScreen`: on every change it removes the modal from the
 * screen that owned it and pushes it onto the newly active one. This also
 * covers the open-before-the-screen-registers case (a lazily loaded screen, or
 * an overlay raised mid-transition): the push simply happens on the next
 * `activeScreen` change instead of being lost forever, which is what left the
 * connection-error overlay's Retry/Exit buttons unreachable by D-pad.
 *
 * `isOpenRef` is intentionally absent: the previous implementation updated
 * `isOpenRef` *after* registering the cleanup function, which left the cleanup
 * closure holding the prior render's stale value and caused a double-remove
 * when `isOpen` transitioned false→true→false in rapid succession.  Using
 * `isOpen` directly (from the effect's dependency array) is both correct and
 * simpler.
 *
 * Usage:
 *   const modal = useMemo(() => new MyModalController(deps), [deps]);
 *   useModalController(modal, isOverlayOpen);
 */
export function useModalController(
  modal: ModalController,
  isOpen: boolean
): void {
  const engine = useNavigationEngine();

  // Redux is the source of truth for which screen receives input; re-running
  // the effect on every change keeps the modal on the live controller.
  const activeScreen = useUnitySelector(selectActiveScreen);

  /**
   * Holds the IScreenController that received the pushModal call.
   *
   * - undefined  → modal is not currently pushed (initial state, or after removal)
   * - value      → the screen that owns this modal right now
   *
   * A ref (not state) because changes must not trigger a re-render — this is
   * pure lifecycle bookkeeping.
   */
  const ownerScreenRef = useRef<IScreenController | undefined>(undefined);

  useEffect(() => {
    if (isOpen) {
      // ── Push path ──────────────────────────────────────────────────────────
      // Capture the active screen at push time (FR-02 fix).
      //
      // Resolved from the `activeScreen` this hook subscribes to rather than
      // from `engine.getActiveScreenController()`: the engine's own
      // active-screen getter is wired by an effect in NavigationProvider, and
      // effects run children-first, so on the first pass — a cold start with
      // the overlay already up — it still answered "" and the push was lost
      // with no second attempt. Both read the same Redux value; this one is
      // not sensitive to effect ordering.
      const screen = engine.getRegistry().getController(activeScreen);
      if (screen !== null && screen !== undefined) {
        screen.pushModal(modal);
        ownerScreenRef.current = screen;
      }
    } else {
      // ── Close path (isOpen → false) ────────────────────────────────────────
      if (ownerScreenRef.current !== undefined) {
        ownerScreenRef.current.removeModal(modal.name);
        ownerScreenRef.current = undefined;
      }
    }

    return () => {
      // ── Cleanup / unmount path ─────────────────────────────────────────────
      // Fires when the component unmounts while the modal is still pushed, or
      // when React re-runs the effect (a dependency changed).  The close path
      // above already cleared ownerScreenRef when isOpen became false, so this
      // guard triggers when the component unmounts while still open, and when
      // the active screen changed while open — releasing the previous owner
      // just before the push path above claims the new one.
      if (ownerScreenRef.current !== undefined) {
        ownerScreenRef.current.removeModal(modal.name);
        ownerScreenRef.current = undefined;
      }
    };
    // modal is intentionally excluded: callers must provide a stable reference
    // via useMemo — the identity of modal must not change between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, engine, activeScreen]);
}
